import { PlaceableTile, TileColor, TileSymbol, isSacredSpring, isSeedTile, isWaveTile, tilesMatch } from '../config/tiles';
import { ShapeId, getShape } from '../config/shapes';
import { SYLPH_COLOR_THRESHOLD, SYLPH_SYMBOL_THRESHOLD } from '../config/game';

export interface Cell {
  tile: PlaceableTile | null;
  restored: boolean;
  active: boolean;
}

export class Grid {
  readonly size: number;
  readonly shapeId: ShapeId;
  private cells: Cell[];
  private activeMask: boolean[];

  constructor(size: number, shapeId?: ShapeId, cells?: Array<{ tile: PlaceableTile | null; restored: boolean }>) {
    this.size = size;
    this.shapeId = shapeId ?? 'square';

    const shape = getShape(this.shapeId);
    this.activeMask = shape.mask.flat();

    if (cells) {
      this.cells = cells.map((c, i) => ({
        tile: c.tile,
        restored: c.restored,
        active: this.activeMask[i],
      }));
    } else {
      this.cells = Array.from({ length: size * size }, (_, i) => ({
        tile: null,
        restored: false,
        active: this.activeMask[i],
      }));
    }
  }

  getCells(): Array<{ tile: PlaceableTile | null; restored: boolean }> {
    return this.cells.map(c => ({ tile: c.tile, restored: c.restored }));
  }

  getCell(row: number, col: number): Cell | null {
    if (row < 0 || row >= this.size || col < 0 || col >= this.size) return null;
    return this.cells[row * this.size + col];
  }

  isCellActive(row: number, col: number): boolean {
    const cell = this.getCell(row, col);
    return cell !== null && cell.active;
  }

  placeTile(row: number, col: number, tile: PlaceableTile): boolean {
    const cell = this.getCell(row, col);
    if (!cell || !cell.active || cell.tile !== null) return false;

    if (!this.isValidPlacement(row, col, tile)) return false;

    cell.tile = tile;
    cell.restored = true;
    return true;
  }

  isCellRestored(row: number, col: number): boolean {
    const cell = this.getCell(row, col);
    return cell !== null && cell.restored;
  }

  isEmpty(): boolean {
    return this.cells.every(cell => !cell.active || cell.tile === null);
  }

  isValidPlacement(row: number, col: number, tile: PlaceableTile): boolean {
    const cell = this.getCell(row, col);
    if (!cell || !cell.active || cell.tile !== null) return false;

    if (isSacredSpring(tile)) return true;
    if (isSeedTile(tile)) return true;
    if (this.isEmpty()) return true;

    const neighbors = this.getOrthogonalNeighbors(row, col);
    const occupiedNeighbors = neighbors.filter(n => n.tile !== null);

    if (occupiedNeighbors.length === 0) return false;

    return occupiedNeighbors.every(n => tilesMatch(tile, n.tile!));
  }

  getCompletedLines(row: number, col: number): { rows: number[]; cols: number[] } {
    const rows: number[] = [];
    const cols: number[] = [];

    const rowActiveCount = Array.from({ length: this.size }, (_, c) =>
      this.getCell(row, c)!.active,
    ).filter(Boolean).length;
    const rowComplete = rowActiveCount >= 2 && Array.from({ length: this.size }, (_, c) => {
      const cell = this.getCell(row, c)!;
      return !cell.active || cell.tile !== null;
    }).every(Boolean);
    if (rowComplete) rows.push(row);

    const colActiveCount = Array.from({ length: this.size }, (_, r) =>
      this.getCell(r, col)!.active,
    ).filter(Boolean).length;
    const colComplete = colActiveCount >= 2 && Array.from({ length: this.size }, (_, r) => {
      const cell = this.getCell(r, col)!;
      return !cell.active || cell.tile !== null;
    }).every(Boolean);
    if (colComplete) cols.push(col);

    return { rows, cols };
  }

  clearCells(positions: Array<{ row: number; col: number }>): void {
    for (const { row, col } of positions) {
      const cell = this.getCell(row, col);
      if (cell && cell.active) cell.tile = null;
    }
  }

  isAllRestored(): boolean {
    return this.cells.every(cell => !cell.active || cell.restored);
  }

  isValidWaveTarget(row: number, col: number): boolean {
    const cell = this.getCell(row, col);
    return cell !== null && cell.active && cell.tile !== null;
  }

  hasValidMove(tile: PlaceableTile): boolean {
    if (isWaveTile(tile)) {
      for (let row = 0; row < this.size; row++) {
        for (let col = 0; col < this.size; col++) {
          if (this.isValidWaveTarget(row, col)) return true;
        }
      }
      return false;
    }
    for (let row = 0; row < this.size; row++) {
      for (let col = 0; col < this.size; col++) {
        if (this.isValidPlacement(row, col, tile)) return true;
      }
    }
    return false;
  }

  isLineMonoColor(rows: number[], cols: number[]): boolean {
    const getColor = (r: number, c: number): TileColor | null | 'wild' | 'skip' => {
      const cell = this.getCell(r, c);
      if (!cell || !cell.active) return 'skip';
      if (!cell.tile) return null;
      if (typeof cell.tile === 'string') return 'wild';
      return cell.tile.color;
    };

    for (const r of rows) {
      let lineColor: TileColor | null = null;
      let mono = true;
      for (let c = 0; c < this.size; c++) {
        const color = getColor(r, c);
        if (color === 'skip' || color === 'wild') continue;
        if (!color) { mono = false; break; }
        if (!lineColor) lineColor = color;
        else if (color !== lineColor) { mono = false; break; }
      }
      if (mono && lineColor) return true;
    }

    for (const c of cols) {
      let lineColor: TileColor | null = null;
      let mono = true;
      for (let r = 0; r < this.size; r++) {
        const color = getColor(r, c);
        if (color === 'skip' || color === 'wild') continue;
        if (!color) { mono = false; break; }
        if (!lineColor) lineColor = color;
        else if (color !== lineColor) { mono = false; break; }
      }
      if (mono && lineColor) return true;
    }

    return false;
  }

  isLineMonoSymbol(rows: number[], cols: number[]): boolean {
    const getSymbol = (r: number, c: number): TileSymbol | null | 'wild' | 'skip' => {
      const cell = this.getCell(r, c);
      if (!cell || !cell.active) return 'skip';
      if (!cell.tile) return null;
      if (typeof cell.tile === 'string') return 'wild';
      return cell.tile.symbol;
    };

    for (const r of rows) {
      let lineSymbol: TileSymbol | null = null;
      let mono = true;
      for (let c = 0; c < this.size; c++) {
        const symbol = getSymbol(r, c);
        if (symbol === 'skip' || symbol === 'wild') continue;
        if (!symbol) { mono = false; break; }
        if (!lineSymbol) lineSymbol = symbol;
        else if (symbol !== lineSymbol) { mono = false; break; }
      }
      if (mono && lineSymbol) return true;
    }

    for (const c of cols) {
      let lineSymbol: TileSymbol | null = null;
      let mono = true;
      for (let r = 0; r < this.size; r++) {
        const symbol = getSymbol(r, c);
        if (symbol === 'skip' || symbol === 'wild') continue;
        if (!symbol) { mono = false; break; }
        if (!lineSymbol) lineSymbol = symbol;
        else if (symbol !== lineSymbol) { mono = false; break; }
      }
      if (mono && lineSymbol) return true;
    }

    return false;
  }

  isLineDiverse(rows: number[], cols: number[]): boolean {
    const checkLine = (tiles: Array<PlaceableTile | null | 'inactive'>): boolean => {
      const colors = new Set<TileColor>();
      const symbols = new Set<TileSymbol>();
      for (const tile of tiles) {
        if (!tile || tile === 'inactive' || typeof tile === 'string') continue;
        colors.add(tile.color);
        symbols.add(tile.symbol);
      }
      return colors.size >= SYLPH_COLOR_THRESHOLD && symbols.size >= SYLPH_SYMBOL_THRESHOLD;
    };

    for (const r of rows) {
      const tiles = Array.from({ length: this.size }, (_, c) => {
        const cell = this.getCell(r, c)!;
        return cell.active ? cell.tile : 'inactive' as const;
      });
      if (checkLine(tiles)) return true;
    }
    for (const c of cols) {
      const tiles = Array.from({ length: this.size }, (_, r) => {
        const cell = this.getCell(r, c)!;
        return cell.active ? cell.tile : 'inactive' as const;
      });
      if (checkLine(tiles)) return true;
    }
    return false;
  }

  private getOrthogonalNeighbors(row: number, col: number): Cell[] {
    const offsets = [[-1, 0], [1, 0], [0, -1], [0, 1]];
    const neighbors: Cell[] = [];
    for (const [dr, dc] of offsets) {
      const cell = this.getCell(row + dr, col + dc);
      if (cell && cell.active) neighbors.push(cell);
    }
    return neighbors;
  }
}
